/**
 * Number and money formatting for invoices.
 *
 * Kept separate and pure because these are the details an auditor and a
 * customer both check first, and because "₹1,50,000.00" is not something
 * `toLocaleString` can be trusted to produce identically on every Node build
 * and container locale. An invoice that groups digits differently depending on
 * which server rendered it is not acceptable, so the grouping is done here.
 */

/** Paise to rupees, with Indian digit grouping: 15000000 → "1,50,000.00". */
export function formatAmount(paise: number): string {
  const negative = paise < 0;
  const rupees = Math.abs(paise) / 100;
  const [whole, fraction] = rupees.toFixed(2).split('.');

  // Indian grouping: the last three digits, then pairs. 1234567 → 12,34,567.
  const last3 = whole.slice(-3);
  const rest = whole.slice(0, -3);
  const grouped = rest
    ? `${rest.replace(/\B(?=(\d{2})+(?!\d))/g, ',')},${last3}`
    : last3;

  return `${negative ? '-' : ''}${grouped}.${fraction}`;
}

/** Basis points to a printable percentage: 1800 → "18". */
export function formatRate(bps: number): string {
  const percent = bps / 100;
  return Number.isInteger(percent) ? String(percent) : percent.toFixed(2).replace(/0+$/, '');
}

const ONES = [
  '', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten',
  'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen',
  'Eighteen', 'Nineteen',
];
const TENS = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

function underThousand(n: number): string {
  if (n === 0) return '';
  if (n < 20) return ONES[n];
  if (n < 100) {
    const rest = n % 10;
    return `${TENS[Math.floor(n / 10)]}${rest ? ` ${ONES[rest]}` : ''}`;
  }
  const rest = n % 100;
  return `${ONES[Math.floor(n / 100)]} Hundred${rest ? ` ${underThousand(rest)}` : ''}`;
}

/**
 * "Rupees Four Lakh Thirty Thousand Seven Hundred Only."
 *
 * Required on a tax invoice, and in the Indian system — lakh and crore, not
 * millions. Paise are named separately when there are any, because rounding
 * them away on a legal document is not this function's decision to make.
 */
export function amountInWords(paise: number): string {
  const rupees = Math.floor(Math.abs(paise) / 100);
  const remainder = Math.abs(paise) % 100;

  if (rupees === 0 && remainder === 0) return 'Rupees Zero Only.';

  const crore = Math.floor(rupees / 10000000);
  const lakh = Math.floor((rupees % 10000000) / 100000);
  const thousand = Math.floor((rupees % 100000) / 1000);
  const rest = rupees % 1000;

  const parts = [
    crore ? `${underThousand(crore)} Crore` : '',
    lakh ? `${underThousand(lakh)} Lakh` : '',
    thousand ? `${underThousand(thousand)} Thousand` : '',
    rest ? underThousand(rest) : '',
  ].filter(Boolean);

  const words = parts.join(' ');
  const paiseWords = remainder ? ` and ${underThousand(remainder)} Paise` : '';

  return `Rupees ${words}${paiseWords} Only.`;
}

/** "12 Oct 2023" — unambiguous in a way that 10/12/2023 is not. */
export function formatDate(date: Date): string {
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${String(date.getDate()).padStart(2, '0')} ${months[date.getMonth()]} ${date.getFullYear()}`;
}

/** Escapes text bound into the invoice HTML. */
export function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
